import type React from 'react';
import { CSSProperties } from 'react';

import { VariantProps, cva } from 'class-variance-authority';

import { DefaultAvatar } from '../assets';
import { cn } from '../lib';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Heading } from './heading';
import { Typography } from './typography';

type IndicatorPosition = Extract<keyof CSSProperties, 'bottom' | 'top'>;

const avatarWithIndicatorVariants = cva('w-10 h-10 overflow-visible', {
  variants: {
    variant: {
      default: '',
      md: 'w-12 h-12',
      lg: 'w-16 h-16',
      xl: 'w-20 h-20',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export interface AvatarWithIndicatorProps
  extends React.ComponentPropsWithoutRef<typeof Avatar>,
    VariantProps<typeof avatarWithIndicatorVariants> {
  avatar?: string;
  indicator?: React.ReactNode;
  defaultAvatar?: React.ReactNode;
  showIndicator?: boolean;
  avatarClassName?: string;
  onIndicatorClick?: () => void;
  indicatorClassName?: string;
  indicatorPosition?: IndicatorPosition;
  fallbackClassName?: string;
}

type AvatarIndicatorProps = Pick<
  AvatarWithIndicatorProps,
  | 'className'
  | 'indicator'
  | 'indicatorPosition'
  | 'variant'
  | 'onIndicatorClick'
>;

const AvatarIndicator = ({
  className,
  indicator,
  onIndicatorClick,
  indicatorPosition,
  variant,
}: AvatarIndicatorProps) => {
  return (
    <div
      onClick={onIndicatorClick}
      className={cn(
        'absolute -right-[8px] flex h-[22px] w-[22px] items-center justify-center rounded-full border-[3px] border-base-500 bg-brand-500',
        {
          '-bottom-[3px]': indicatorPosition === 'bottom',
          '-bottom-[1px] -right-[1px]':
            variant === 'xl' && indicatorPosition === 'bottom',
          '-top-[3px]': indicatorPosition === 'top',
          'h-8 w-8 border-[3px]': variant === 'xl',
        },
        className,
      )}
    >
      {indicator ?? ''}
    </div>
  );
};

export const AvatarWithIndicator = ({
  avatar,
  indicator,
  defaultAvatar = <DefaultAvatar className="h-10 w-10 rounded-full" />,
  className,
  avatarClassName,
  indicatorClassName,
  indicatorPosition = 'bottom',
  showIndicator = true,
  onIndicatorClick,
  fallbackClassName,
  variant,
  ...props
}: AvatarWithIndicatorProps) => {
  return (
    <div className={className}>
      <div className="relative">
        <Avatar
          className={avatarWithIndicatorVariants({
            variant,
            className: avatarClassName,
          })}
          {...props}
        >
          <AvatarImage src={avatar} alt="avatar" className="rounded-full" />
          <AvatarFallback className="bg-base-300">
            {variant === 'xl' && (
              <Heading variant="xs" className="text-base-500">
                {defaultAvatar}
              </Heading>
            )}
            {variant !== 'xl' && (
              <Typography className={cn('text-base-500', fallbackClassName)}>
                {defaultAvatar}
              </Typography>
            )}
          </AvatarFallback>
        </Avatar>

        {showIndicator && (
          <AvatarIndicator
            onIndicatorClick={onIndicatorClick}
            className={indicatorClassName}
            indicatorPosition={indicatorPosition}
            indicator={indicator}
            variant={variant}
          />
        )}
      </div>
    </div>
  );
};
